var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Collapse, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useUpdatePlayer } from "../ApiProvider";
import { PlayerAttributeFields, PlayerBioFields, PlayerPositionFields, } from "../players/PlayerMetadataFields";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { FormActions } from "../utils/FormActions";
var ATTRIBUTE_NAMES = [
    "heading",
    "passing",
    "positioning",
    "defending",
    "finishing",
    "dribbling",
    "shooting",
    "tackling",
    "crossing",
    "leadership",
    "communication",
    "speed",
    "fitness",
    "keeping",
];
var profileDefaults = function (player, fullProfile) {
    // Full (a player): the required identity fields start blank and are validated
    // on submit, forcing an active choice instead of a silent US/GK/Right default.
    // Light (just managing): everything is optional, so default to the player's
    // existing valid values — untouched fields still submit cleanly.
    var identity = fullProfile
        ? { nationality: "", positionOne: "", foot: "" }
        : {
            nationality: player.nationality || "US",
            positionOne: player.positionOne || "GK",
            foot: player.foot || "Right",
        };
    var defaults = __assign(__assign({}, identity), { positionTwo: player.positionTwo || "", bio: player.bio || "" });
    ATTRIBUTE_NAMES.forEach(function (name) {
        var _a;
        defaults[name] = (_a = player[name]) !== null && _a !== void 0 ? _a : 50;
    });
    return defaults;
};
/**
 * The player-profile form used across onboarding (the wizard and the
 * invite-accept bridge). For a player (`fullProfile`) nationality, positions
 * and foot are shown and required. For a non-playing manager it's a light,
 * fully-optional version: just nationality + bio, with positions and attributes
 * collapsed behind an "optional" expander. Title/subtitle are overridable so
 * each entry point can frame it ("Create your profile", "You're in!", ...).
 */
export var PlayerProfileStep = function (_a) {
    var player = _a.player, submitLabel = _a.submitLabel, fullProfile = _a.fullProfile, onDone = _a.onDone, title = _a.title, subtitle = _a.subtitle;
    var _b = useForm({
        defaultValues: profileDefaults(player, fullProfile),
    }), control = _b.control, handleSubmit = _b.handleSubmit;
    var _c = __read(useState(false), 2), error = _c[0], setError = _c[1];
    var _d = __read(useState(false), 2), showDetails = _d[0], setShowDetails = _d[1];
    var mutation = useUpdatePlayer({
        id: player.id,
        onMutate: function () { return setError(false); },
        onSuccess: function (data) {
            onDone();
            return data.data;
        },
        onError: function () { return setError(true); },
    });
    // On a failed save, bring the first invalid field into view.
    var scrollToFirstError = function () {
        requestAnimationFrame(function () {
            var _a, _b, _c;
            (_c = (_b = (_a = document
                .getElementById("onboarding-profile-form")) === null || _a === void 0 ? void 0 : _a.querySelector(".Mui-error")) === null || _b === void 0 ? void 0 : _b.scrollIntoView) === null || _c === void 0 ? void 0 : _c.call(_b, { behavior: "smooth", block: "center" });
        });
    };
    var heading = title !== null && title !== void 0 ? title : (fullProfile ? "Create your profile" : "A bit about you (optional)");
    var subheading = subtitle !== null && subtitle !== void 0 ? subtitle : (fullProfile
        ? "This helps teammates and managers know your position and style. You can always change it later."
        : "Optional — add as much or as little as you like. You can fill this in later.");
    return (_jsxs(Box, { component: "form", id: "onboarding-profile-form", noValidate: true, onSubmit: handleSubmit(function (data) { return mutation.mutate(data); }, scrollToFirstError), children: [_jsx(Typography, { variant: "h5", sx: { fontWeight: 700 }, children: heading }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: subheading }), _jsx(PlayerBioFields, { control: control, required: fullProfile }), fullProfile ? (_jsxs(_Fragment, { children: [_jsx(PlayerPositionFields, { control: control }), _jsx(PlayerAttributeFields, { control: control })] })) : (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: function () { return setShowDetails(function (show) { return !show; }); }, endIcon: _jsx(ExpandMoreIcon, { sx: {
                                transform: showDetails ? "rotate(180deg)" : "none",
                                transition: "transform 0.2s",
                            } }), sx: { mt: 2 }, children: showDetails
                            ? "Hide player details"
                            : "Add player details — positions & ratings (optional)" }), _jsxs(Collapse, { in: showDetails, unmountOnExit: true, children: [_jsx(PlayerPositionFields, { control: control, required: false }), _jsx(PlayerAttributeFields, { control: control })] })] })), _jsx(ErrorSnackbar, { open: error, message: "Failed to save your profile. Please try again.", onClose: function () { return setError(false); } }), _jsx(FormActions, { submitLabel: submitLabel, isSubmitting: mutation.isLoading })] }));
};
